import httpService from '../httpService';
import { CRMIntegration, CrmMapping, CRMType, CrmUser } from '../../types/CRMIntegration';
import { RootState } from '../../redux/reducers';
import { store } from '../../redux/store';

const crmService = {
  /**
   * Gets the list of CRM integrations for the current user's community.
   * @async
   * @returns {Promise<{ crm: CRMType; connected: boolean }[]>} A Promise that resolves with an array of CRM integrations.
   * @throws {Error} If there was an error getting the CRM integrations.
   */
  getIntegrations: async (): Promise<CRMIntegration[]> => {
    const { selectedBusiness } = (store.getState() as RootState).application;
    const response = await httpService.get(`/networks/businesses/${selectedBusiness}/integrations`);
    const { data }: { data: CRMIntegration[] } = response;
    return data;
  },

  /**
   * Gets the list of CRM integrations for the current user's community.
   * @async
   * @returns {Promise<{ crm: CRMType; connected: boolean }[]>} A Promise that resolves with an array of CRM integrations.
   * @throws {Error} If there was an error getting the CRM integrations.
   */
  getUsers: async (crmIntegration: CRMIntegration): Promise<CrmUser[]> => {
    if (!crmIntegration.connected) return Promise.resolve([]);
    const { selectedBusiness } = (store.getState() as RootState).application;
    return httpService
      .get<CrmUser[]>(`/networks/businesses/${selectedBusiness}/integrations/users`)
      .then(({ data }) => data);
  },

  getCrmMappings: async (crmIntegration: CRMIntegration): Promise<CrmMapping[]> => {
    if (!crmIntegration.connected) return Promise.resolve([]);
    const { selectedBusiness } = (store.getState() as RootState).application;
    return httpService
      .get<CrmMapping[]>(`/networks/businesses/${selectedBusiness}/integrations/mappings`)
      .then(({ data }) => data);
  },

  inviteCrmUsers: (crm: CRMType, crmUserIds: string[]): Promise<void> => {
    const { selectedBusiness } = (store.getState() as RootState).application;
    return httpService.post<void>(`/networks/businesses/${selectedBusiness}/integrations/invite`, { crm, crmUserIds }).then(() => {});
  },

  /**
   * Connects the current user's community to a CRM.
   * @async
   * @param {CRMIntegration} crmIntegration - The crmIntegration object containing the CRM to connect to.
   * @returns {Promise<void>} A Promise that resolves when the connection has been established.
   * @throws {Error} If there was an error connecting to the CRM.
   */
  connectToCrm: async (crmIntegration: CRMIntegration, authParam: unknown = {}): Promise<void> => {
    if (crmIntegration.connected) return;
    if (crmIntegration.loginUrl) {
      window.location.href = crmIntegration.loginUrl;
    } else {
      const { selectedBusiness } = (store.getState() as RootState).application;
      await httpService.post<void>(`/networks/businesses/${selectedBusiness}/integrations`, { crm: crmIntegration.crm, auth: authParam });
    }
  },

  /**
   * Updates the current user's community CRM integration.
   * @async
   * @param crmIntegration - The CRM integration to update.
   * @param data - The data to update the CRM integration with.
   * @returns A Promise that resolves when the CRM integration has been updated.
   * @throws {Error} If there was an error connecting to the CRM.
   */
  updateCrmIntegration: async (
    crmIntegration: CRMIntegration,
    data: { auth?: unknown; additional?: unknown },
  ): Promise<void> => {
    if (!crmIntegration.connected) return;
    const { selectedBusiness } = (store.getState() as RootState).application;
    await httpService.patch<void>(`/networks/businesses/${selectedBusiness}/integrations`, { crm: crmIntegration.crm, ...data });
  },

  handleCrmCallback: async (): Promise<void> => {
    await httpService.post<void>(
      '/auth/oauth/callback?code=na1-f946-c177-4628-9534-e55f7ff6aace&state=1218==DHUBSPOT',
    );
  },

  /**
   * Disconnects the current user's community from a CRM.
   * @async
   * @param {CRMIntegration} crmIntegration - The crmIntegration object containing the CRM to disconnect from.
   * @returns {Promise<void>} A Promise that resolves when the connection has been disconnected.
   * @throws {Error} If there was an error disconnecting from the CRM.
   */
  disconnectFromCrm: async (crmIntegration: CRMIntegration): Promise<void> => {
    if (!crmIntegration.connected) return;
    const { selectedBusiness } = (store.getState() as RootState).application;
    await httpService.delete(`/networks/businesses/${selectedBusiness}/integrations`, { data: { crm: crmIntegration.crm } });
  },

}

export default crmService;